import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LogoComponent } from '../../shared/logo/logo.component';
import { DashboardService } from '../../core/dashboard/dashboard.service';
import { Dashboard, DashboardDestaque, DashboardAtividade } from '../../core/dashboard/dashboard.model';
import { AuthService } from '../../core/auth/auth.service';

interface KpiCard {
  label: string;
  value: string;
  hint: string;
  delta?: { dir: 'up' | 'down' | 'flat'; texto: string };
}

@Component({
  selector: 'app-home',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterLink, LogoComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  private readonly dashSvc = inject(DashboardService);
  private readonly auth = inject(AuthService);

  protected readonly loading = signal(true);
  protected readonly erro = signal<string | null>(null);
  // Saudacao do usuario logado (login + papel vindos do JWT), nao mais hardcoded.
  protected readonly usuario = computed(() => ({
    nome: this.primeiroNome(this.auth.username()),
    perfil: this.auth.isAdmin() ? 'Administrador' : 'Conferência',
  }));

  // "BREITNER.S" -> "Breitner"; "bruno.santiago" -> "Bruno".
  private primeiroNome(login: string | null): string {
    if (!login) return 'usuário';
    const base = login.split(/[._@\s]/)[0];
    return base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : 'usuário';
  }

  protected filtroInicio = this.toIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  protected filtroFim = this.toIso(new Date());

  protected readonly saudacao = computed(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  });

  protected readonly hojeExtenso = computed(() => {
    return new Date().toLocaleDateString('pt-BR', {
      weekday: 'long', day: '2-digit', month: 'long',
    });
  });

  protected readonly dash = signal<Dashboard | null>(null);

  protected readonly periodoLabel = computed(() => {
    if (this.filtroInicio && this.filtroFim) return 'no periodo';
    return 'este mes';
  });

  protected readonly kpis = computed<KpiCard[]>(() => {
    const d = this.dash();
    if (!d) return [];
    const lbl = this.periodoLabel();
    return [
      {
        label: 'Cupons ativos',
        value: String(d.cuponsAtivos),
        hint: `de ${d.cuponsCadastrados} cadastrados`,
        delta: d.cuponsNovosSemana > 0
          ? { dir: 'up' as const, texto: `+${d.cuponsNovosSemana} esta semana` }
          : { dir: 'flat' as const, texto: 'sem alteracao' },
      },
      {
        label: 'Aplicacoes',
        value: this.fmtN(d.aplicacoesMes),
        hint: lbl,
        delta: this.calcDelta(d.aplicacoesMes, d.aplicacoesMesAnterior),
      },
      {
        label: 'Total descontado',
        value: this.fmtR(d.totalDescontadoMes),
        hint: lbl,
        delta: this.calcDelta(d.totalDescontadoMes, d.totalDescontadoMesAnterior),
      },
      {
        label: 'Ticket medio',
        value: this.fmtR(d.ticketMedio),
        hint: 'por aplicacao',
        delta: this.calcDelta(d.ticketMedio, d.ticketMedioMesAnterior),
      },
    ];
  });

  protected readonly destaques = computed<DashboardDestaque[]>(() => this.dash()?.destaques ?? []);
  protected readonly atividade = computed<DashboardAtividade[]>(() => this.dash()?.atividadeRecente ?? []);

  protected readonly resumoHero = computed(() => {
    const d = this.dash();
    if (!d) return '';
    return `${d.cuponsAtivos} cupons ativos e ${this.fmtN(d.aplicacoesMes)} aplicacoes registradas neste mes.`;
  });

  ngOnInit(): void {
    this.carregarDashboard();
  }

  protected aplicarFiltro(): void {
    this.carregarDashboard();
  }

  protected limparFiltro(): void {
    this.filtroInicio = this.toIso(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    this.filtroFim = this.toIso(new Date());
    this.carregarDashboard();
  }

  private carregarDashboard(): void {
    this.loading.set(true);
    this.erro.set(null);
    const inicio = this.filtroInicio || undefined;
    const fim = this.filtroFim || undefined;
    this.dashSvc.obter(inicio, fim).subscribe({
      next: res => {
        this.dash.set(res.data ?? null);
        this.loading.set(false);
      },
      error: e => {
        this.erro.set(`Falha ao carregar dashboard: ${e.status ?? ''} ${e.message}`);
        this.loading.set(false);
      },
    });
  }

  protected fmtR(v: number): string {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  protected fmtN(v: number): string {
    return v.toLocaleString('pt-BR');
  }
  protected fmtData(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const agora = new Date();
    const diff = agora.getTime() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'Agora';
    if (min < 60) return `Ha ${min} min`;
    const horas = Math.floor(min / 60);
    if (horas < 24) return `Ha ${horas} h`;
    const dias = Math.floor(horas / 24);
    if (dias === 1) return 'Ontem';
    return `Ha ${dias} dias`;
  }

  private toIso(d: Date): string {
    return d.toISOString().slice(0, 10);
  }

  private calcDelta(atual: number, anterior: number): KpiCard['delta'] {
    if (anterior === 0 && atual === 0) return { dir: 'flat', texto: 'sem dados' };
    if (anterior === 0) return { dir: 'up', texto: 'novo' };
    const pct = Math.round(((atual - anterior) / anterior) * 100);
    if (pct === 0) return { dir: 'flat', texto: 'estavel' };
    if (pct > 0) return { dir: 'up', texto: `+${pct}%` };
    return { dir: 'down', texto: `${pct}%` };
  }
}
